// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/Etiquetas.tsx
//  Geração de etiquetas de código de barras para produtos.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { Barcode, Plus, Trash2, Printer, Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/apiClient';

// ── Types ────────────────────────────────────────────────────────────────────

interface ProdutoSelecionado {
  produtoId: number;
  descricao: string;
  codigoBarras: string | null;
  precoPadrao: number;
  quantidade: number;
}

interface LayoutInfo {
  id: number;
  nome: string;
  descricao: string;
  etiquetasPorPagina: number;
}

const LAYOUTS: LayoutInfo[] = [
  { id: 1, nome: 'Pimaco 6080 (3x10)', descricao: '30 etiquetas/pg — 26x15mm', etiquetasPorPagina: 30 },
  { id: 2, nome: 'Pimaco 6081 (3x8)', descricao: '24 etiquetas/pg — 33x22mm', etiquetasPorPagina: 24 },
  { id: 3, nome: 'Pimaco 6182 (2x7)', descricao: '14 etiquetas/pg — 50x30mm', etiquetasPorPagina: 14 },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

// ── Component ────────────────────────────────────────────────────────────────

export default function Etiquetas() {
  const { toast } = useToast();
  const [busca, setBusca] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [layout, setLayout] = useState(2); // Default: 3x8
  const [mostrarPreco, setMostrarPreco] = useState(true);
  const [mostrarCodInterno, setMostrarCodInterno] = useState(false);
  const [produtos, setProdutos] = useState<ProdutoSelecionado[]>([]);
  const [gerando, setGerando] = useState(false);

  // ── Buscar produto por código de barras ou ID ──────────────────────────────

  const buscarProduto = useCallback(async () => {
    if (!busca.trim()) return;
    setBuscando(true);
    try {
      // A API filtra por código, descrição, código de barras ou GTIN
      const termo = busca.trim();
      const { data } = await apiClient.get('/api/produtos', {
        params: { termo, pagina: 1, tamanhoPagina: 5 },
      });

      type ProdutoBusca = {
        id: number; descricao: string; codigoBarras?: string; gtin?: string; precoPadrao: number;
      };
      let itens: ProdutoBusca[] = data.itens ?? data;
      if (!itens?.length) {
        toast({ title: 'Produto não encontrado', variant: 'destructive' });
        return;
      }

      // Código ou código de barras exato (leitor): adiciona só esse produto
      const exatos = itens.filter((p) =>
        String(p.id) === termo
        || p.codigoBarras?.toLowerCase() === termo.toLowerCase()
        || p.gtin?.toLowerCase() === termo.toLowerCase());
      if (exatos.length) itens = exatos;

      // Adiciona o primeiro resultado (ou todos se forem poucos)
      const novos: ProdutoSelecionado[] = itens.map((p) => ({
        produtoId: p.id,
        descricao: p.descricao,
        codigoBarras: p.gtin || p.codigoBarras || null,
        precoPadrao: p.precoPadrao,
        quantidade: 1,
      }));

      // Evita duplicatas
      setProdutos((prev) => {
        const existing = new Set(prev.map((x) => x.produtoId));
        const unique = novos.filter((n) => !existing.has(n.produtoId));
        return [...prev, ...unique];
      });
      setBusca('');
    } catch {
      toast({ title: 'Erro ao buscar produto', variant: 'destructive' });
    } finally {
      setBuscando(false);
    }
  }, [busca, toast]);

  // ── Atualizar quantidade ───────────────────────────────────────────────────

  const setQuantidade = (produtoId: number, qtd: number) => {
    setProdutos((prev) =>
      prev.map((p) => p.produtoId === produtoId ? { ...p, quantidade: Math.max(1, Math.min(500, qtd)) } : p)
    );
  };

  // ── Remover produto ────────────────────────────────────────────────────────

  const removerProduto = (produtoId: number) => {
    setProdutos((prev) => prev.filter((p) => p.produtoId !== produtoId));
  };

  // ── Gerar PDF ──────────────────────────────────────────────────────────────

  const gerarEtiquetas = useCallback(async () => {
    if (produtos.length === 0) {
      toast({ title: 'Adicione ao menos um produto', variant: 'destructive' });
      return;
    }

    setGerando(true);
    try {
      const response = await apiClient.post('/api/etiquetas/gerar', {
        layout,
        mostrarPreco,
        mostrarCodigoInterno: mostrarCodInterno,
        itens: produtos.map((p) => ({ produtoId: p.produtoId, quantidade: p.quantidade })),
      }, { responseType: 'blob', timeout: 30000 });

      // Download do PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `etiquetas_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({ title: 'Etiquetas geradas com sucesso!' });
    } catch {
      toast({ title: 'Erro ao gerar etiquetas', variant: 'destructive' });
    } finally {
      setGerando(false);
    }
  }, [produtos, layout, mostrarPreco, mostrarCodInterno, toast]);

  // ── Total de etiquetas ─────────────────────────────────────────────────────

  const totalEtiquetas = produtos.reduce((s, p) => s + p.quantidade, 0);
  const layoutInfo = LAYOUTS.find((l) => l.id === layout)!;
  const totalPaginas = Math.ceil(totalEtiquetas / layoutInfo.etiquetasPorPagina);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
          <Barcode className="h-8 w-8" />
          Etiquetas de Codigo de Barras
        </h1>
        <p className="text-muted-foreground mt-1">
          Gere etiquetas com codigo de barras e preco para impressao em folhas adesivas.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna da esquerda: configurações */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configuracoes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Layout da Etiqueta</Label>
              <Select value={String(layout)} onValueChange={(v) => setLayout(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LAYOUTS.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>
                      {l.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{layoutInfo.descricao}</p>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="mostrar-preco">Mostrar preco</Label>
              <Switch id="mostrar-preco" checked={mostrarPreco} onCheckedChange={setMostrarPreco} />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="mostrar-cod">Mostrar codigo interno</Label>
              <Switch id="mostrar-cod" checked={mostrarCodInterno} onCheckedChange={setMostrarCodInterno} />
            </div>

            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total de etiquetas:</span>
                <span className="font-semibold">{totalEtiquetas}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Paginas estimadas:</span>
                <span className="font-semibold">{totalPaginas || '—'}</span>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={gerarEtiquetas}
              disabled={gerando || produtos.length === 0}
            >
              {gerando ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Gerando...</>
              ) : (
                <><Printer className="h-4 w-4 mr-2" />Gerar PDF</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Coluna da direita: produtos */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Produtos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Busca */}
            <div className="flex gap-2">
              <Input
                placeholder="Buscar por nome, codigo de barras ou ID..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && buscarProduto()}
              />
              <Button variant="secondary" onClick={buscarProduto} disabled={buscando}>
                {buscando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            {/* Tabela de produtos selecionados */}
            {produtos.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Barcode className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum produto adicionado.</p>
                <p className="text-sm">Busque produtos acima para adicionar a lista de impressao.</p>
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead>Cod. Barras</TableHead>
                      <TableHead className="text-right">Preco</TableHead>
                      <TableHead className="text-center w-28">Qtd Etiquetas</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {produtos.map((p) => (
                      <TableRow key={p.produtoId}>
                        <TableCell className="font-medium">{p.descricao}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {p.codigoBarras || <span className="text-muted-foreground italic">Sem codigo</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCur(p.precoPadrao)}</TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min={1}
                            max={500}
                            value={p.quantidade}
                            onChange={(e) => setQuantidade(p.produtoId, parseInt(e.target.value) || 1)}
                            className="w-20 text-center mx-auto"
                          />
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => removerProduto(p.produtoId)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
